/* eslint-disable react/display-name */
import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { clone } from 'ramda';
import { connect } from 'react-redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Cropper } from 'react-cropper';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import {
  Button,
  Checkbox,
  DataGrid,
  Drawer,
  PopupMenu,
  TextInputWithLabel,
  Loader,
  FileUploader,
  DialogBox,
  CardsGrid,
  Toast
} from 'quantux-ui';
import { addAppAlert, toggleDmDrawer, selectDmUser } from '../../actions';
import { blue, gray, green, salmon } from '../../theme';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import { BASE_URL, titleCase, DRAWER_BREAK_POINT, USA_STATES, debugLogToConsole, getPathParts, isValidUSPhoneNumber } from '../../utils';

let controller = new AbortController();
const DOCS_PER_PAGE = 15;

const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;

const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  // borderBottom: '0.5px solid lime',
  // borderRight: '0.5px solid lime',
  lineHeight: '14px'
};

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const headerDefs = [
  // Name (First, middle and last)
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Name',
    field: 'name',
    key: 'name',

    isCustomFilter: true,
    showFilterHint: true,
    filterHint: 'Tony will think of something',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    // colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' },
    colStyles: { width: 323, minWidth: 323, maxWidth: 323, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text, getText) => {
      // debugLogToConsole(text);
      // `${activeRecord.displayName}/${activeRecord.npi}/${activeRecord._id}/${credentials}/${specialty}|${avatarUrl}`
      const cData = text.split('|');
      const data = cData[0].split('/');
      if (data.length > 2) {
        return (
          <div className='fx'>
            <div>
              <img
                src={cData[1]}
                alt='user avatar'
                style={{ maxHeight: '32px' }}
              />
            </div>
            <div
              className='ml-2'
              style={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center'
              }}
            >
              <div style={{ color: '#0080FF', cursor: 'pointer' }}>
                {`${getText(data[0])}${data[3] ? ', ' + data[3] : ''}`}
              </div>
              <div style={{ color: '#0080FF', cursor: 'pointer' }}>{data[4] || ''}</div>
              {/* <div style={{ color: '#0080FF', cursor: 'pointer' }}>{data[1]}</div> */}
            </div>
          </div>
        );
      } else {
        return (
          <div
            style={{
              display: 'flex',
              color: blue[500],
              cursor: 'pointer'
            }}
          >
            <div>
              <img
                src={cData[1]}
                alt='user avatar'
                style={{ maxHeight: '32px' }}
              />
            </div>
            <div
              className='ml-2'
              style={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center'
              }}
            >
              {getText(data[0])}
            </div>
          </div>
        );
      }
    }
  },
  // Credentials
  {
    visible: false,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Credentials',
    field: 'credentials',
    key: 'credentials',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Speciality
  {
    visible: false,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Specialty',
    field: 'specialty',
    key: 'specialty',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Primary address ()
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Primary Address',
    field: 'primaryAddress',
    key: 'primaryAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    // colStyles: { width: 300, minWidth: 300, maxWidth: 300, textAlign: 'left' },
    colStyles: { width: 423, minWidth: 423, maxWidth: 423, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      if (text.length > 0) {
        // Format address and zip
        const addTmp = text.split(' ');
        let zip = addTmp.pop();
        zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
        // Generate Google map url
        // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
        const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
        return (
          <div className="fx fx__v-center">
            <a
              target="_blank"
              href={addressUrl}
              rel="noreferrer"
              className="mr-2"
              style={{ fontSize: '16px' }}
            >
              <i className="fa fa-map-marker" />
            </a>
            <a
              target="_blank"
              href={addressUrl}
              rel="noreferrer"
              className="mr-2"
            >
              <div>
                {addTmp.join(' ') + ' ' + zip}
              </div>
            </a>
          </div>
        );
      } else {
        return <div />;
      }
    }
  },
  // Mobile (mobile)
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Mobile',
    field: 'mobile',
    key: 'mobile',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 150, minWidth: 150, maxWidth: 150, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // const tmp = text.replaceAll('-', '');
      const tmp = text.replace(/-/g, '');
      return (
        <div>
          <a
            href={`tel:+1${tmp}`}
          >
            {`${tmp.substr(0, 3)}-${tmp.substr(3, 3)}-${tmp.substr(6, 4)}`}
          </a>
        </div>
      );
    }
  },
  // Last updated
  {
    visible: true,
    dataType: 'date',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Last Updated',
    field: 'lastUpdate',
    key: 'lastUpdated',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'dateRange',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 180, minWidth: 180, maxWidth: 180, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  }
];

const UsersDataGrid = forwardRef((props, ref) => {
  // #region useState Variables
  const gridInitDataLoadRef = useRef(true);
  const [showLoader, setShowLoader] = useState(false);
  const [gridHeaderDefs, setGridHeaderDefs] = useState(headerDefs);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // For add user/provider drawers and copper
  const [isBannerCropperDrawerOpen, toggleBannerCropperDrawer] = useState(false);
  const [isAvatarCropperDrawerOpen, toggleAvatarCropperDrawer] = useState(false);
  const [isAddUserDrawerVisible, toggleAddUserDrawer] = useState(false);
  const [showUserSearchDrawer, setShowUserSearchDrawer] = useState(false);
  const [isProvider, setIsProvider] = useState(true);
  const [providerFullName, setProviderFullName] = useState('');
  const [providerCredentials, setProviderCredentials] = useState('');
  const [providerSpeciality, setProviderSpeciality] = useState('');
  const [providerTaxonomy, setProviderTaxonomy] = useState({});
  const [displayName, setDisplayName] = useState('');
  const [isDisplayNameValid, checkDisplayNameValidity] = useState(false);
  const [isDisplayNamePristine, setDisplayNamePristineState] = useState(true);
  const [userName, setUserName] = useState('');
  const [isUserNameValid, checkUserNameValidity] = useState(false);
  const [isUserNamePristine, setUserNamePristineState] = useState(true);
  const [isUserNameDuplicate, setUserNameDuplicateStatus] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mailingStreetAddress, setMailingStreetAddress] = useState('');
  const [mailingSuite, setMailingSuite] = useState('');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingState, setMailingState] = useState('');
  const [mailingzip, setMailingZip] = useState('');
  // --
  const [primaryStreetAddress, setPrimaryStreetAddress] = useState('');
  const [primarySuite, setPrimarySuite] = useState('');
  const [primaryCity, setPrimaryCity] = useState('');
  const [primaryState, setPrimaryState] = useState('');
  const [primaryzip, setPrimaryZip] = useState('');
  // --
  const [phone, setPhone] = useState('');
  const [isPhoneValid, checkPhoneValidity] = useState(false);
  const [isPhonePristine, setPhonePristineState] = useState(true);
  const [fax, setFax] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [bannerImageUrl, setBannerImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
  const [avatarImageUrl, setAvatarImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailValid, checkEmailValidity] = useState(false);
  const [isEmailPristine, setEmailPristineState] = useState(true);
  const [isEmailDuplicate, setEmailDuplicateStatus] = useState(true);
  const [mobile, setMobile] = useState('');
  const [isMobileValid, checkMobileValidity] = useState(false);
  const [isMobilePristine, setMobilePristineState] = useState(true);
  const [isMobileDuplicate, setMobileDuplicateStatus] = useState(true);
  const [npi, setNpi] = useState('');
  const [website, setWebsite] = useState('');
  const [isLastNameSearchMenuOpen, toggleLastNameSearchMenu] = useState(false);
  const [lastNameSearchResults, setLastNameSearchResults] = useState([]);
  const [canAddUser, setCanAddUser] = useState(false);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 550);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 143);
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [isDeleteWarningDialogOpen, toggleDeleteWarningDialog] = useState(false);
  // Cards grid
  const [showCardsGrid, setShowCardsGrid] = useState(false);
  // Grid toolbar search
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectAllRecords, setSelectAllRecords] = useState(false);
  const [resize, setResize] = useState(0);
  // --
  const [isUserSelected, setIsUserSelected] = useState(false);
  // -- New User
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searchNpi, setSearchNpi] = useState('');
  const [showNpiErrMessage, setShowNpiErrMessage] = useState(false);
  const [canSearch, setCanSearch] = useState(false);
  const [npiErrMsg, setNpiErrMsg] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [showStateMenu, setShowStateMenu] = useState(false);
  const [searchingProviders, setSearchingProviders] = useState(false);
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [showExistingUserWarning, setShowExistingUserWarning] = useState(false);
  const [showNpiCopyMessage, setShowNpiCopyMessage] = useState(false);
  window.psr = providerSearchResults;
  // Refs
  const gridParentRef = useRef();
  const stateRef = useRef();
  // #endregion

  const history = useHistory();
  const { path } = useRouteMatch();

  // -------------------------- Styles --------------------------
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      width: `${gridWidth}px`,
      overflow: 'hidden',
      height: showCardsGrid ? `${window.innerHeight - 90}px` : ''
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    usersSearchMenu: {
      width: '350px',
      background: gray[700],
      overflow: 'hidden'
    },
    drawerContainer: {
      background: gray[700],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    imageUploaderContainer: {
      position: 'relative',
      maxHeight: '454px'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    userSearchItems: {
      cursor: 'pointer',
      ':hover': {
        background: gray[800]
      }
    },
    userSearchItemsIcon: {
      width: '40px',
      height: '40px',
      background: blue[500],
      color: 'white',
      fontSize: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '5px'
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    },
    duplicateUserErrMsg: {
      color: salmon[500],
      fontSize: '14px',
      width: '130px',
      textAlign: 'center',
      paddingTop: '4px'
    },
    npiErrMessage: {
      color: salmon[500],
      fontSize: '14px',
    },
    npiErrMessageContainer: {
      textAlign: 'right',
      paddingRight: '8px',
      paddingTop: '4px'
    },
    mItem: {
      backgroundColor: '#103A60',
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      width: '100%',
      font: 'bold 14px Lato',
      paddingLeft: '4px',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: 'black',
        color: 'white'
      }
    },
  });

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.98 * gridParentRefWidth);
      setGridWidth(Math.min(tmp, 1111));
      setGridHeight(window.innerHeight - 134);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  // Effects
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
    };
  }, []);

  // Check for duplicate username
  useEffect(() => {
    if (userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0) {
      const url = new URL(`${BASE_URL}/users/exists/username/${userName}`);
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          setUserNameDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [userName]);

  // Check for duplicate email
  useEffect(() => {
    // if (isFirstRender.current) {
    //   isFirstRender.current = false;
    //   return;
    // }
    if (isEmail(email)) {
      const url = new URL(`${BASE_URL}/users/exists/email/${email}`);
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          checkEmailValidity(isEmail(email));
          setEmailDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
    // const ddF = setTimeout(() => { searchUsersByLastName(); }, 300);
    // return () => clearTimeout(ddF);
  }, [email]);

  // Check for duplicate mobile
  useEffect(() => {
    // if (isFirstRender.current) {
    //   isFirstRender.current = false;
    //   return;
    // }
    if (isMobilePhone(mobile)) {
      const url = new URL(`${BASE_URL}/users/exists/mobile/${mobile}`);
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          checkMobileValidity(isMobilePhone(mobile));
          setMobileDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
    // const ddF = setTimeout(() => { searchUsersByLastName(); }, 300);
    // return () => clearTimeout(ddF);
  }, [mobile]);

  // Can add user flag check
  useEffect(() => {
    const value =
      firstName.length > 2 &&
      lastName.length > 2 &&
      displayName.length >= 2 && displayName.length <= 40 &&
      userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0 &&
      !isUserNameDuplicate &&
      isEmailValid &&
      !isEmailDuplicate &&
      isMobileValid &&
      !isMobileDuplicate;
    setCanAddUser(value);
  }, [
    firstName,
    lastName,
    displayName,
    userName,
    isUserNameDuplicate,
    mailingStreetAddress,
    mailingSuite,
    mailingCity,
    mailingState,
    mailingzip,
    email,
    isEmailValid,
    isEmailDuplicate,
    mobile,
    isMobileValid,
    isMobileDuplicate,
    phone,
    isPhoneValid
  ]);

  // Apply filter effects hook
  useEffect(() => {
    if (!gridInitDataLoadRef.current) {
      debugLogToConsole('Getting users data .. filters, currentPage, sorting effect');
      getData();
    }
  }, [filters, currentPage, sorting]);

  useEffect(() => {
    if (resize < props.reSize) {
      setResize(props.reSize);
      handleWindowResize();
    }
  }, [props.reSize]);

  useEffect(() => {
    if (searchNpi.length === 0) {
      setShowNpiErrMessage(false);
      setNpiErrMsg('');
    } else if (isNaN(parseInt(searchNpi))) {
      setNpiErrMsg('NPI should only have digits');
      setShowNpiErrMessage(true);
    } else {
      if (searchNpi.length < 10 || searchNpi.length > 10 || searchNpi.indexOf(' ') > -1) {
        setShowNpiErrMessage(true);
        setNpiErrMsg('Atleast 10 digits');
      } else {
        setShowNpiErrMessage(false);
        setNpiErrMsg('');
      }
    }
  }, [searchNpi]);

  useEffect(() => {
    if (searchNpi.length === 0 && searchLastName.length === 0) {
      setCanSearch(false);
    } else {
      if (searchLastName.length > 0 && searchNpi.length === 0) {
        setCanSearch(true);
      } else if (searchLastName.length === 0 && searchNpi.length > 0) {
        setCanSearch(!showNpiErrMessage);
      } else {
        setCanSearch(!showNpiErrMessage);
      }
    }
  }, [showNpiErrMessage, searchLastName, searchNpi]);

  const getData = async () => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    formData.append('filters', JSON.stringify(filters));
    formData.append('sorting', JSON.stringify(sorting));

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const response = await fetch(`${BASE_URL}/users/getUsers`, requestOptions).catch(err => {
      debugLogToConsole(err);
      debugLogToConsole('Failed to get users data and count.');
    });

    gridInitDataLoadRef.current = false;

    if (response) {
      const usersDataResponse = await response.json();
      if (usersDataResponse && usersDataResponse.success) {
        const { usersCount } = usersDataResponse;
        const data = [];
        for (let i = 0; i < usersDataResponse.users.length; i++) {
          const activeRecord = usersDataResponse.users[i];
          const add1 = activeRecord.primaryAddress && activeRecord.primaryAddress.streetAddress1
            ? activeRecord.primaryAddress.streetAddress1
            : '';
          const suite = activeRecord.primaryAddress && activeRecord.primaryAddress.suite ? activeRecord.primaryAddress.suite : '';
          const city = activeRecord.primaryAddress && activeRecord.primaryAddress.city
            ? activeRecord.primaryAddress.city
            : '';
          const state = activeRecord.primaryAddress && activeRecord.primaryAddress.state
            ? activeRecord.primaryAddress.state
            : '';
          const zip = activeRecord.primaryAddress && activeRecord.primaryAddress.zip
            ? activeRecord.primaryAddress.zip
            : '';
          const avatarUrl = activeRecord.avatarImageUrl || '';
          const credentials = activeRecord.credentials ? activeRecord.credentials : 'NA';
          const specialty = activeRecord.specialty ? activeRecord.specialty : 'NA';
          data.push({
            id: activeRecord._id,
            name: !activeRecord.npi
              ? `${activeRecord.displayName}/${activeRecord._id}|${avatarUrl}`
              : `${activeRecord.displayName}/${activeRecord.npi}/${activeRecord._id}/${credentials}/${specialty}|${avatarUrl}`,
            credentials,
            specialty,
            primaryAddress: add1.length > 0 && city.length > 0 && state.length > 0 && zip.length > 0
              ? `${add1}${suite.length > 0 ? `${suite} ` : ''} ${city}, ${state} ${zip}`
              : '',
            mobile: activeRecord.mobile,
            lastUpdate: activeRecord.createdOn ? new Date(activeRecord.createdOn).toLocaleDateString() : (new Date()).toLocaleDateString()
          });
        }
        setGridData(data);
        setShowLoader(false);
        setDocsCount(usersCount);
      }
    }
  };

  const getUniqueUsername = (firstName, lastName) => {
    const url = new URL(`${BASE_URL}/users/getUsername`);
    const params = { firstName: firstName.charAt(0), lastName };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        // console.log(data);
        checkUserNameValidity(data.userName.length >= 2 && data.userName <= 20);
        setUserName(data.userName);
        setEmail(`${data.userName}@ggmail.com`);
        checkEmailValidity(isEmail(`${data.userName}@ggmail.com`));
      })
      .catch(error => console.log('error', error));
  };

  // Reset state when Add user drawer is closed
  const resetSearchProviderDrawerState = () => {
    setSearchFirstName('');
    setSearchLastName('');
    setSearchNpi('');
    setSearchCity('');
    setSearchState('');
    setSearchZip('');
    setSearchingProviders(false);
    setProviderSearchResults([]);
  };

  const resetState = () => {
    toggleBannerCropperDrawer(false);
    toggleAvatarCropperDrawer(false);
    setDisplayName('');
    checkDisplayNameValidity(false);
    setDisplayNamePristineState(true);
    setUserName('');
    checkUserNameValidity(false);
    setUserNamePristineState(true);
    setUserNameDuplicateStatus(true);
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setMailingStreetAddress('');
    setMailingSuite('');
    setMailingCity('');
    setMailingState('');
    setMailingZip('');
    setPrimaryStreetAddress('');
    setPrimarySuite('');
    setPrimaryCity('');
    setPrimaryState('');
    setPrimaryZip('');
    setPhone('');
    checkPhoneValidity(false);
    setPhonePristineState(true);
    setFax('');
    setProfileDescription('');
    setBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
    // setAvatarImageUrl('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
    setAvatarImageUrl('https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
    setShowBannerCropper(false);
    setIsBannerUploading(false);
    setShowAvatarCropper(false);
    setIsAvatarUploading(false);
    setEmail('');
    checkEmailValidity(false);
    setEmailPristineState(true);
    setEmailDuplicateStatus(true);
    setMobile('');
    checkMobileValidity(false);
    setMobilePristineState(true);
    setMobileDuplicateStatus(true);
    setNpi('');
    setWebsite('');
    toggleLastNameSearchMenu(false);
    setLastNameSearchResults([]);
    setCanAddUser(false);
    setIsProvider(true);
    setProviderFullName('');
    setProviderCredentials('');
    setProviderSpeciality('');
    setIsUserSelected(false);
    // Search provider drawer state vars
    resetSearchProviderDrawerState();
  };

  // Select search items from the last name search menu
  const selectUser = (data) => {
    if (data.hasMedprosProfile) {
      setShowExistingUserWarning(true);
      setTimeout(() => {
        setShowExistingUserWarning(false);
      }, 5000);
    } else {
      setShowUserSearchDrawer(false);
      toggleAddUserDrawer(true);
      const { number = '', basic, taxonomies, addresses } = data;
      let mAddress = {};
      let pAddress = {};
      const {
        first_name: firstName = '',
        middle_name: middleName = '',
        last_name: lastName = '',
        credential = ''
      } = basic;

      let taxonomy = {};
      let specialty = '';

      if (Array.isArray(taxonomies) && taxonomies.length > 0) {
        const taxIdx = taxonomies.findIndex(el => el.primary);
        if (taxIdx > -1) {
          taxonomy = taxonomies[taxIdx];
        }
      }

      if (taxonomy && Object.keys(taxonomy).length > 0) {
        specialty = taxonomy.desc;
      }

      // Address and Telephone (mobile)
      let tNumber = ''; // User telephone number (also used as mobile number)

      if (Array.isArray(addresses) && addresses.length > 0) {
        for (let i = 0; i < addresses.length; i++) {
          // disable-eslint
          if (addresses[i].telephone_number) {
            tNumber = addresses[i].telephone_number;
          }
          if (addresses[i].address_purpose === 'MAILING') {
            mAddress = addresses[i];
          }
          if (addresses[i].address_purpose === 'LOCATION') {
            pAddress = addresses[i];
          }
          // enable-eslint
        }
      }

      const {
        address_1: mStreetAddress = '',
        city: mailingCity = '',
        postal_code: mPCode = '',
        state: mailingStreet = ''
      } = mAddress;

      const {
        address_1: pStreetAddress = '',
        city: primaryCity = '',
        postal_code: pPCode = '',
        state: primaryStreet = ''
      } = mAddress;

      setIsProvider(number && number.length > 0);
      setIsUserSelected(true);
      setMailingStreetAddress(titleCase(mStreetAddress));
      setMailingCity(titleCase(mailingCity));
      setMailingState(mailingStreet.toUpperCase());
      setMailingZip(mPCode);
      setPrimaryStreetAddress(titleCase(pStreetAddress));
      setPrimaryCity(titleCase(primaryCity));
      setPrimaryState(primaryStreet.toUpperCase());
      setPrimaryZip(pPCode);
      setPhone(tNumber);
      setMobile(tNumber);
      setFirstName(titleCase(firstName));
      setMiddleName(titleCase(middleName));
      setLastName(titleCase(lastName));
      setProviderFullName(`${titleCase(firstName)}${middleName !== '' ? ` ${titleCase(middleName)}` : ''} ${titleCase(lastName)}${credential !== '' ? `, ${credential}` : ''}`);
      setNpi(number.toString());
      toggleLastNameSearchMenu(false);
      getUniqueUsername(firstName, lastName);
      setDisplayName(titleCase(`${firstName} ${lastName}`).substr(0, 40));
      setProviderCredentials(credential.toUpperCase());
      setProviderSpeciality(titleCase(specialty));
      setProviderTaxonomy(taxonomy);
    }
  };

  const addUserProfile = () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('npi', npi);
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('middleName', middleName);
    formData.append('displayName', displayName);
    formData.append('userName', userName);
    // Primary address
    formData.append('streetAddress', primaryStreetAddress);
    formData.append('suite', primarySuite);
    formData.append('city', primaryCity);
    formData.append('state', primaryState);
    formData.append('zip', primaryzip);
    // Mailing address
    formData.append('mailingStreetAddress', mailingStreetAddress);
    formData.append('mailingSuite', mailingSuite);
    formData.append('mailingCity', mailingCity);
    formData.append('mailingState', mailingState);
    formData.append('mailingZip', mailingzip);
    // ---
    formData.append('email', email);
    formData.append('mobile', mobile);
    formData.append('phone', phone);
    formData.append('fax', fax);
    formData.append('website', website);
    formData.append('avatarImageUrl', avatarImageUrl);
    formData.append('bannerImageUrl', bannerImageUrl);
    formData.append('profileDescription', profileDescription);
    formData.append('credentials', providerCredentials);
    formData.append('specialty', providerSpeciality);
    formData.append('taxonomy', JSON.stringify(providerTaxonomy));

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/addUser2`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          getData();
          toggleAddUserDrawer(false);
          resetState();
        }
      })
      .catch(error => console.log('error', error));
  };

  const deleteUserProfile = (_id) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('_id', _id);

    const requestOptions = {
      method: 'DELETE',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/deleteUser`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  // #region Grid Toolbar handler functions

  const handleOnClickToggleGrid = () => {
    setShowCardsGrid(!showCardsGrid);
  };

  const handleOnClickToggleUserDrawer = () => {
    // toggleAddUserDrawer(true);
    setShowUserSearchDrawer(true);
  };
  // #endregion

  // #region Data Grid Handler Functions
  const handleOnClickCell = (col, value, recordId) => {
    // [[ REFACTOR ]]
    if (col === 'name') {
      const currentSection = getPathParts(path)[0] || '';
      const tmp = value.split('|');
      localStorage.setItem('provider', tmp[0]);
      const idx = gridData.findIndex(el => el.name === value);
      const providerId = gridData[idx].id;
      if (currentSection !== 'provider') {
        const appLocs = JSON.parse(localStorage.getItem('appLocs')) || [];
        appLocs.push('/admin/users');
        localStorage.setItem('appLocs', JSON.stringify(appLocs));
      }
      localStorage.setItem('lastBookmark', '/admin/users');
      history.push(`/provider/${providerId}/profile`);
    }
  };

  const handleOnClickResetFilters = () => {
    console.log('Grid reset function not implemented yet');
    setUserSearchTerm('');
    setFilters({});
  };

  const handleOnEditUserSearchTerm = (e) => {
    const { value } = e.target;
    setUserSearchTerm(value);
    if (value === '') {
      handleOnClickResetFilters();
    }
  };

  const handleOnClickDownloadData = () => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/users/downloadUsers`, requestOptions)
      .then(response => response.blob())
      .then((blob) => {
        const file = window.URL.createObjectURL(blob);
        window.location.assign(file);
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPopupMenu = (e, idx) => {
    // console.log(e.pageX, e.pageY, idx);
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickShowDetailsMenuItem = () => {
    const tmp = (gridData[popupMenuIndex].name).split('|');
    const providerId = gridData[popupMenuIndex].id;
    localStorage.setItem('provider', tmp[0]);
    localStorage.setItem('lastBookmark', '/admin/users');
    history.push(`/provider/${providerId}/profile`);
  };

  const handleOnClickDeleteMenuItem = () => {
    toggleDeleteWarningDialog(true);
  };

  const handleOnClickSendMessage = () => {
    const id = (gridData[popupMenuIndex]).id;
    props.selectDmUser(id);
    props.toggleDmDrawer(true);
  };

  const handleOnDeleteUser = () => {
    const id = (gridData[popupMenuIndex]).id;
    toggleGridEllipsisMenu(false);
    toggleDeleteWarningDialog(false);
    deleteUserProfile(id);
  };
  // #endregion

  // #region Edit Profile Form (Edit Handlers) & Save Profile

  const handleOnEditNpi = (e) => {
    const { value } = e.target;
    setNpi(value);
  };

  const handleOnEditFirstName = (e) => {
    const { value } = e.target;
    setFirstName(value);
  };

  const handleOnEditLastName = (e) => {
    const { value } = e.target;
    setLastName(value);
  };

  const handleOnEditMiddleName = (e) => {
    const { value } = e.target;
    setMiddleName(value);
  };

  const handleOnEditDisplayName = (e) => {
    const { value } = e.target;
    setDisplayNamePristineState(false);
    checkDisplayNameValidity(value.length >= 2 && value.length <= 40);
    setDisplayName(value);
  };

  const handleOnEditUserName = (e) => {
    const { value } = e.target;
    setUserNamePristineState(false);
    checkUserNameValidity(value.length >= 2 && value.length <= 20 && value.indexOf(' ') < 0);
    setUserName(value);
  };

  const handleOnEditMailingStreetAddress = (e) => {
    const { value } = e.target;
    setMailingStreetAddress(value);
  };

  const handleOnEditMailingSuite = (e) => {
    const { value } = e.target;
    setMailingSuite(value);
  };

  const handleOnEditMailingCity = (e) => {
    const { value } = e.target;
    setMailingCity(value);
  };

  const handleOnEditMailingState = (e) => {
    const { value } = e.target;
    setMailingState(value);
  };

  const handleOnEditMailingZip = (e) => {
    const { value } = e.target;
    setMailingZip(value);
  };

  const handleOnEditPrimaryStreetAddress = (e) => {
    const { value } = e.target;
    setPrimaryStreetAddress(value);
  };

  const handleOnEditPrimarySuite = (e) => {
    const { value } = e.target;
    setPrimarySuite(value);
  };

  const handleOnEditPrimaryCity = (e) => {
    const { value } = e.target;
    setPrimaryCity(value);
  };

  const handleOnEditPrimaryState = (e) => {
    const { value } = e.target;
    setPrimaryState(value);
  };

  const handleOnEditPrimaryZip = (e) => {
    const { value } = e.target;
    setPrimaryZip(value);
  };

  const handleOnEditEmail = (e) => {
    const { value } = e.target;
    setEmailPristineState(false);
    checkEmailValidity(isEmail(value));
    setEmail(value);
  };

  const handleOnEditMobile = (e) => {
    const { value } = e.target;
    setMobilePristineState(false);
    // const tmpV = isValidUSPhoneNumber(value);
    checkMobileValidity(isMobilePhone(value));
    setMobile(value);
  };

  const handleOnEditPhone = (e) => {
    const { value } = e.target;
    setPhonePristineState(false);
    checkPhoneValidity(isMobilePhone(value, 'en-US'));
    setPhone(value);
  };

  const handleOnEditFax = (e) => {
    const { value } = e.target;
    setFax(value);
  };

  const handleOnEditWebsite = (e) => {
    const { value } = e.target;
    setWebsite(value);
  };

  const handleOnChangeProfileDescription = (e) => {
    const { value } = e.target;
    setProfileDescription(value);
  };
  // #endregion

  // #region Banner image crop and file upload
  const uploadBanner = () => {
    const formData = new FormData();
    const img = bannerFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedBanner = (blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'croppedBanner.png');
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped banner image url', res.fileUrl);
          setBannerImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsBannerUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileSelected = () => {
    if (bannerFileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', bannerFileInputRef.current.files[0]);
      // console.log('typeof img', typeof bannerFileInputRef.current.files[0]);
      setIsBannerUploading(true);
      uploadBanner();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickBannerCrop = () => {
    const imageElement = bannerCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerBannerRef.current.src = url;
      uploadCroppedBanner(blob);
      toggleBannerCropperDrawer(false);
    });
  };
  // #endregion

  // #region Avatar image crop and file upload

  const uploadAvatar = () => {
    const formData = new FormData();
    const img = bannerFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedAvatar = (blob) => {
    const formData = new FormData();
    formData.append('file', blob);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped avatar image url', res.fileUrl);
          setAvatarImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsAvatarUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileSelected = () => {
    if (avatarFileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', avatarFileInputRef.current.files[0]);
      // console.log('typeof img', typeof avatarFileInputRef.current.files[0]);
      setIsAvatarUploading(true);
      uploadAvatar();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickAvatarCrop = () => {
    const imageElement = avatarCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerAvatarRef.current.src = url;
      uploadCroppedAvatar(blob);
      toggleAvatarCropperDrawer(false);
    });
  };
  // #endregion

  const handleOnClickSaveUserProfile = () => {
    if (!canAddUser) {
      return;
    }
    addUserProfile();
  };

  // Refs
  const lastNameSearchMenu = React.createRef();
  const drawerBannerRef = React.createRef();
  const drawerAvatarRef = React.createRef();
  const bannerCropperRef = React.createRef();
  const bannerFileInputRef = React.createRef();
  const avatarCropperRef = React.createRef();
  const avatarFileInputRef = React.createRef();
  const gridEllipsisMenu = React.createRef();

  // Last name search results
  const userSearchItems = [];
  if (lastNameSearchResults.length > 0) {
    lastNameSearchResults.forEach((el) => {
      userSearchItems.push(
        <div
          key={el.number}
          className={`fx p-2 ${css(styles.userSearchItems)}`}
          onClick={() => { selectUser(el); }}
        >
          {/* User Avatar */}
          <div className={css(styles.userSearchItemsIcon)}>
            <i className='fa fa-user' />
          </div>
          {/* Name, NPI and username (if available) */}
          <div
            className='ml-2'
            style={{ fontSize: '12px' }}
          >
            <div className='fx'>
              <div>
                <span>{`${titleCase(el.basic.last_name)}, ${titleCase(el.basic.first_name)}`}</span>
                <span style={{ color: gray[400] }}>
                  {el.basic.credential ? ` ${el.basic.credential}` : ''}
                </span>
              </div>
              <div className='ml-2' style={{ color: blue[500] }}>
                {el.userName ? `@${el.userName}` : ''}
              </div>
            </div>
            <div>
              {el.number}
            </div>
          </div>
        </div>
      );
    });
  }

  // #region Data Grid Event Handlers

  const handleFilterTypeUpdate = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    if (updatedFilters[field]) {
      updatedFilters[field].filterType = value;
      updatedFilters[field].value = value !== 'dateRange' ? '' : ':';
    }
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filterType = value;
        el.filter = true;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    setFilters(updatedFilters);
  };

  const handleOnApplyFilter = (dataType, filterType, field, filterValue) => {
    setFilters((prvState) => {
      const prvStateClone = clone(prvState);
      if (filterValue === '' && Object.keys(prvStateClone).indexOf(field) > -1) {
        delete prvStateClone[field];
      }
      const newFilter = {};
      if (filterValue !== '' && filterValue !== ':') {
        newFilter[field] = { dataType, filterType, value: filterValue };
      }

      const result = {
        ...prvStateClone,
        ...newFilter
      };
      console.log('--------------------- Filters ---------------------');
      console.log(result);
      return result;
    });
  };

  const handleOnClickUserSearch = () => {
    // dataType, filterType, field, filterValue
    handleOnApplyFilter('string', 'exactMatch', 'name', userSearchTerm);
  };

  const handleOnFilterToggle = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filter = value;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    if (updatedFilters[field]) {
      delete updatedFilters[field];
    }
    setFilters(updatedFilters);
  };

  const handleOnPinChange = (field, pinValue) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.pin = pinValue;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
  };

  const handleOnLoadPage = (page) => {
    console.log('Loading page... ');
    setCurrentPage(page);
  };

  const handleOnSortOrderChanged = (field) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.sortOrder = el.sortOrder === 0
          ? el.sortOrder + 1
          : el.sortOrder + (-el.sortOrder * 2);
      } else {
        el.sortOrder = 0;
      }
    });

    const sort = {};
    for (let i = 0; i < updatedGridHeaderDefs.length; i++) {
      if (updatedGridHeaderDefs[i].sortOrder === 0) {
        continue;
      } else {
        if (updatedGridHeaderDefs[i].field === 'name') {
          // sort['lastName'] = updatedGridHeaderDefs[i].sortOrder;
          sort.lastName = updatedGridHeaderDefs[i].sortOrder;
        } else if (updatedGridHeaderDefs[i].field === 'lastUpdate') {
          // sort['createdOn'] = updatedGridHeaderDefs[i].sortOrder;
          sort.createdOn = updatedGridHeaderDefs[i].sortOrder;
        } else {
          sort[updatedGridHeaderDefs[i].field] = updatedGridHeaderDefs[i].sortOrder;
        }
      }
    }
    setGridHeaderDefs(updatedGridHeaderDefs);
    setSorting(sort);
  };

  // #endregion

  // #region User Search
  const handleOnEditSearchFirstName = (e) => {
    const { value } = e.target;
    setSearchFirstName(value);
  };

  const handleOnEditSearchLastName = (e) => {
    const { value } = e.target;
    setSearchLastName(value);
  };

  const handleOnEditSearchNpi = (e) => {
    const { value } = e.target;
    setSearchNpi(value);
  };

  const handleOnEditSearchCity = (e) => {
    const { value } = e.target;
    setSearchCity(value);
  };

  const handleOnEditSearchZip = (e) => {
    const { value } = e.target;
    setSearchZip(value);
  };

  const handleOnClickResetUserSearch = () => {
    setSearchFirstName('');
    setSearchLastName('');
    setSearchNpi('');
    setSearchCity('');
    setSearchState('');
    setSearchZip('');
    setProviderSearchResults([]);
  };

  const handleOnClickSearchUsers = () => {
    if (searchingProviders) {
      controller.abort();
      resetSearchProviderDrawerState();
      return;
    }

    controller = new AbortController();
    const url = new URL(`${BASE_URL}/users/searchNppes`);
    const params = {
      firstName: searchFirstName,
      lastName: searchLastName,
      npi: searchNpi,
      state: searchState === 'ALL' ? '' : searchState,
      city: searchCity,
      zip: searchZip
    };
    url.search = new URLSearchParams(params).toString();

    setSearchingProviders(true);
    fetch(url, { signal: controller.signal })
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        setSearchingProviders(false);

        if (data.users.some(p => p.enumeration_type === 'NPI-2')) {
          props.addAppAlert({
            headerText: 'Warning',
            headerIcon: 'fa fa-bell',
            bodyText: 'Only type 1 NPI search allowed!',
            showAlert: true,
            autoDismiss: true,
            autoDismissTime: 5000
          });
          setProviderSearchResults([]);
          setSearchFirstName('');
          setSearchLastName('');
          setSearchNpi('');
          setSearchCity('');
          setSearchState('');
          setSearchZip('');
        } else {
          /* eslint-disable */
          const tmpUser = {
            addresses: [
              {
                address_1: '',
                address_purpose: "MAILING",
                address_type: 'DOM',
                city: '',
                country_code: 'US',
                country_name: 'United States',
                postal_code: '',
                state: '',
                telephone_number: ''
              }
            ],
            basic: {
              first_name: searchFirstName,
              last_name: searchLastName
            },
            hasMedprosProfile: false,
            isProvider: false,
            taxonomies: [
              {
                code: '',
                desc: '',
                license: '',
                primary: true,
                state: '',
                taxonomy_group: ''
              }
            ]
          };
          /* eslint-enable */
          const tmpArr = data.users;
          if (searchLastName.length > 0) {
            data.users.unshift(tmpUser);
          }

          data.users.forEach(user => {
            if (user.number && user.number.length > 0) {
              user.isProvider = true;
            }
          });
          setProviderSearchResults(tmpArr);
          data.users.sort((a, b) => b.hasMedprosProfile - a.hasMedprosProfile);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickCreateProfileAndAddProvider = (data) => {
    selectUser(data);
  };
  // #endregion

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Existing user warning */}
      {showExistingUserWarning &&
        <Toast
          id='toast_1'
          top="41"
          bottom={null}
          headerText='Warnring'
          headerIcon='fa fa-exclamation-triangle'
          bodyText='User already exist!'
          onDismiss={() => {
            setShowExistingUserWarning(false);
          }}
          showFooter={false}
        />
      }

      {/* User search drawer */}
      {showUserSearchDrawer &&
        <Drawer
          placement="right"
          visible={showUserSearchDrawer}
          toggleDrawer={() => {
            setShowUserSearchDrawer(false);
            resetSearchProviderDrawerState();
          }}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-user' />
                <div className='noselect'>Add User</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  setShowUserSearchDrawer(false);
                  resetSearchProviderDrawerState();
                }}
              />
            </div>
            {/* Middle Area */}
            <div className={css(styles.drawerContentArea)}>
              <div className='pl-2 pr-2'>
                <div className='mt-3'>
                  {/* First Name, Last Name and NPI */}
                  <div className='fx'>
                    {/* First Name */}
                    <TextInputWithLabel
                      value={searchFirstName}
                      titleText="First Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={108}
                      onChange={handleOnEditSearchFirstName}
                    />
                    {/* Last Name */}
                    <TextInputWithLabel
                      value={searchLastName}
                      className='ml-2'
                      titleText="Last Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={108}
                      placeholder={!showNpiErrMessage && searchNpi.length === 10 ? null : 'Required'}
                      onChange={handleOnEditSearchLastName}
                    />
                    {/* NPI */}
                    <TextInputWithLabel
                      value={searchNpi}
                      className='ml-2'
                      titleText="NPI"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={100}
                      onChange={handleOnEditSearchNpi}
                    />
                  </div>
                  {showNpiErrMessage &&
                    <div
                      className={css(styles.npiErrMessageContainer)}
                    >
                      <div className={css(styles.npiErrMessage)}>
                        {npiErrMsg}
                      </div>
                    </div>
                  }
                  {/* City, State & Zip */}
                  <div className='fx' style={{ alignItems: 'flex-end' }}>
                    <TextInputWithLabel
                      value={searchCity}
                      titleText="City"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={192}
                      onChange={handleOnEditSearchCity}
                    />
                    <div
                      ref={stateRef}
                      style={{ position: 'relative' }}
                    >
                      <div
                        className='ml-2'
                        onClick={() => setShowStateMenu(true)}
                        style={{
                          height: '24px',
                          width: '43px',
                          // background: blue[900],
                          background: '#103A60',
                          color: blue[200],
                          fontSize: '12px',
                          padding: '2px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          bottom: '-2px'
                        }}
                      >
                        {`${searchState.length === 0 ? 'ALL' : searchState} `}
                        <i
                          style={{ fontSize: '16px' }}
                          className={`ml-1 fa ${showStateMenu ? 'fa-caret-up' : 'fa-caret-down'}`}
                        />
                      </div>
                      <PopupMenu
                        top={28}
                        left={11}
                        zIndex='1550'
                        position="absolute"
                        visible={showStateMenu}
                        parent={stateRef}
                        onOutsideClick={() => setShowStateMenu(false)}
                      >
                        <div
                          style={{
                            background: '#0a3250',
                            width: '128px',
                            height: '200px',
                            overflowY: 'auto'
                          }}
                        >
                          {USA_STATES.map(el => (
                            <div
                              key={el.code}
                              className={css(styles.mItem)}
                              onClick={() => {
                                setSearchState(el.code);
                                setShowStateMenu(false);
                              }}
                              style={{
                                height: '32px',
                                borderBottom: '1px solid #001824',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {el.name}
                            </div>
                          ))}
                        </div>
                      </PopupMenu>
                    </div>
                    <TextInputWithLabel
                      value={searchZip}
                      className='ml-2'
                      titleText="Zip"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={80}
                      onChange={handleOnEditSearchZip}
                    />
                  </div>
                  {/* Reset and Search Buttons */}
                  <div className='mt-4 fx fx__align-right'>
                    {/* Reset Button */}
                    <Button
                      btnType='primary'
                      isOutlineBtn
                      isDisabled={!canSearch}
                      onClick={handleOnClickResetUserSearch}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        width: '100px',
                        height: '32px'
                      }}
                    >
                      Reset
                    </Button>
                    {/* Search Button */}
                    <Button
                      className='ml-2'
                      isDisabled={!canSearch}
                      showLoader={searchingProviders}
                      btnType='primary'
                      onClick={handleOnClickSearchUsers}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        width: '240px',
                        height: '32px'
                      }}
                    >
                      {searchingProviders ? 'Searching... Click to Cancel' : 'Search'}
                    </Button>
                  </div>
                </div>
                {/* Providers Cards */}
                <div
                  className='mt-2 pl-2 pr-2'
                  style={{
                    height: 'calc(100vh - 213px)',
                    overflowY: 'auto'
                  }}
                >
                  {providerSearchResults.map(el => {
                    console.log(el);
                    const {
                      first_name: fName,
                      last_name: lName,
                      credential = ''
                    } = el.basic;
                    const taxIdx = el.taxonomies.findIndex(el => el.primary);
                    const addIdx = el.addresses.findIndex(el => el.address_purpose === 'MAILING');
                    const {
                      address_1: streetAddress = '',
                      city = '',
                      state = '',
                      postal_code: zip = ''
                    } = el.addresses[addIdx];

                    return (
                      <div
                        key={`${el.number}__provider`}
                        className='mt-2 p-2'
                        style={{
                          background: blue[700],
                          borderRadius: '4px',
                          borderTop: `1px solid ${blue[600]}`,
                          fontSize: '14px',
                          fontWeight: '700',
                          minHeight: '49px'
                        }}
                      >
                        {/* Data */}
                        <div style={{ flexGrow: '1' }}>
                          <a
                            target='_blank'
                            rel="noreferrer"
                            href={`https://npiregistry.cms.hhs.gov/provider-view/${el.number}`}
                            style={{ color: blue[400] }}
                          >
                            <div>
                              {`${titleCase(fName)} ${titleCase(lName)}${credential.length > 0 ? ' , ' + credential : ''}`}
                            </div>
                            <div style={{ display: el.isProvider ? '' : 'none' }}>
                              {`${el.number && el.number.length > 0 ? el.number : ''}${taxIdx > -1 ? ' / ' + el.taxonomies[taxIdx].desc : ''}`}
                            </div>
                          </a>
                          {/* Primary Address */}
                          <div
                            className='mt-1'
                            style={{
                              color: blue[200]
                            }}
                          >
                            <div
                              style={{
                                display: streetAddress.length > 0 && city.length > 0 && state.length > 0 && zip.length > 0
                                  ? ''
                                  : 'none'
                              }}
                            >
                              <div>
                                Primary Address
                              </div>
                              <div className='fx mt-1'>
                                {/* Address Icon */}
                                <div>
                                  <svg
                                    width="14"
                                    height="16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M13.152 3.704a6.208 6.208 0 0 0-1.504-1.93A7.154 7.154 0 0 0 9.464.474 7.359 7.359 0 0 0 6.86 0h-.004C5.964 0 5.088.16 4.25.475a7.154 7.154 0 0 0-2.184 1.298A6.202 6.202 0 0 0 .562 3.704 5.336 5.336 0 0 0 0 6.08c0 1.14.277 2.312.825 3.484.453.97 1.094 1.949 1.907 2.91 1.453 1.72 3.09 2.95 3.725 3.4a.69.69 0 0 0 .8 0c.636-.45 2.274-1.68 3.726-3.4.81-.961 1.453-1.94 1.906-2.91.548-1.172.825-2.346.825-3.484a5.33 5.33 0 0 0-.562-2.375Zm-6.295.523c-1.264 0-2.29 1.037-2.29 2.314 0 1.276 1.026 2.314 2.29 2.314S9.148 7.817 9.148 6.54c0-1.277-1.027-2.314-2.29-2.314Zm0 5.894c.948 0 1.838-.372 2.507-1.048A3.575 3.575 0 0 0 10.4 6.541c0-.957-.368-1.856-1.037-2.532A3.51 3.51 0 0 0 6.857 2.96c-.948 0-1.838.372-2.507 1.048A3.578 3.578 0 0 0 3.313 6.54c0 .958.368 1.857 1.037 2.532a3.507 3.507 0 0 0 2.507 1.048Z"
                                      fill="#00C8FF"
                                    />
                                  </svg>
                                </div>
                                {/* Address */}
                                <div className='ml-2'>
                                  <div>{el.addresses[addIdx].address_1}</div>
                                  <div>
                                    {titleCase(el.addresses[addIdx].city)}
                                    {el.addresses[addIdx].state.length > 0
                                      ? `, ${el.addresses[addIdx].state.toUpperCase()}`
                                      : ''
                                    }
                                    {el.addresses[addIdx].postal_code && el.addresses[addIdx].postal_code.length > 0
                                      ? ` ${el.addresses[addIdx].postal_code}`
                                      : ''
                                    }
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Create profile button */}
                        <div className='mt-2'>
                          <Button
                            isDisabled={el.hasMedprosProfile}
                            style={{
                              maxWidth: '309px',
                              minWidth: '309px',
                              maxHeight: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '4px',
                              fontSize: '14px'
                            }}
                            onClick={() => {
                              handleOnClickCreateProfileAndAddProvider(el);
                            }}
                          >
                            {el.hasMedprosProfile ? 'Duplicate User' : 'Create Profile'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      }
      {/* Add user drawer */}
      {isAddUserDrawerVisible &&
        <Drawer
          placement="right"
          visible={isAddUserDrawerVisible}
          toggleDrawer={() => {
            toggleAddUserDrawer(false);
            resetState();
          }}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          {/* Drawer Container */}
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-user' />
                <div className='noselect'>Create User Profile</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  toggleAddUserDrawer(false);
                  resetState();
                }}
              />
            </div>
            {/* Banner */}
            <div className={css(styles.drawerBanner)}>
              <img
                ref={drawerBannerRef}
                src={bannerImageUrl}
                alt="cropped-banner"
                className={css(styles.drawerBannerImg)}
              />
              {/* Camera icon */}
              <div
                onClick={() => { toggleBannerCropperDrawer(true); }}
                className={css(styles.drawerBannerCameraIcon)}
              >
                <i className="fa fa-camera" />
              </div>
            </div>
            {/* Middle area */}
            <div className={css(styles.drawerContentArea)}>
              {/* IsProvider Checkbox */}
              <div className='ml-2 mt-0'>
                <Checkbox
                  text='Is Provider?'
                  isChecked={isProvider}
                  isDisabled
                />
              </div>
              {/* NPI  */}
              <div
                style={{
                  paddingLeft: '8px',
                  position: 'relative',
                  marginTop: '-2px',
                  // display: isProvider ? 'block' : 'none'
                  display: 'none'
                }}
                ref={lastNameSearchMenu}
              >
                <div className='fx' style={{ position: 'relative' }}>
                  <TextInputWithLabel
                    value={npi}
                    isDisabled={isUserSelected}
                    titleText='NPI (Required for providers)'
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={342}
                    onChange={handleOnEditNpi}
                    onClick={() => {
                      toggleLastNameSearchMenu(true);
                    }}
                  />
                  {isUserSelected &&
                    <i
                      className='fa fa-times'
                      style={{
                        position: 'relative',
                        top: '20px',
                        left: '-12px',
                        color: salmon[500],
                        cursor: 'pointer'
                      }}
                      onClick={() => resetState()}
                    />
                  }
                </div>
                <PopupMenu
                  top={44}
                  right={0}
                  zIndex='1550'
                  position="absolute"
                  visible={isLastNameSearchMenuOpen}
                  parent={lastNameSearchMenu}
                  onOutsideClick={() => { toggleLastNameSearchMenu(false); }}
                >
                  <div className={css(styles.usersSearchMenu)}>
                    <div
                      className='centered-text pt-2 pb-2'
                      style={{
                        borderBottom: `1px solid ${blue[700]}`
                      }}
                    >
                      Search Users by NPI
                    </div>
                    <div
                      className='mt-1 mb-1'
                      style={{
                        maxHeight: '250px',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                      }}
                    >
                      {userSearchItems}
                    </div>
                  </div>
                </PopupMenu>
              </div>
              {/* Last, Middle and First name */}
              {!isProvider
                ? <div
                  className='fx pl-2'
                  style={{ fontSize: '14px', fontWeight: '700' }}
                >
                  {/* First Name */}
                  <div>
                    <TextInputWithLabel
                      value={firstName}
                      titleText="First Name*"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={110}
                      onChange={handleOnEditFirstName}
                    />
                  </div>
                  {/* Middle name */}
                  <div className='ml-2 mr-2'>
                    <TextInputWithLabel
                      value={middleName}
                      titleText="Middle Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={108}
                      onChange={handleOnEditMiddleName}
                    />
                  </div>
                  {/* Last name */}
                  <div>
                    <TextInputWithLabel
                      value={lastName}
                      titleText="Last Name*"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={108}
                      onChange={handleOnEditLastName}
                    />
                  </div>
                </div>
                : <div
                  className='ml-2 mt-1'
                  style={{ color: blue[300] }}
                >
                  <div>{providerFullName}</div>
                  <div
                    style={{ position: 'relative' }}
                    onClick={() => {
                      navigator.clipboard.writeText(npi);
                      setShowNpiCopyMessage(true);
                      setTimeout(() => {
                        setShowNpiCopyMessage(false);
                      }, 3000);
                    }}
                  >
                    {npi !== '' ? `NPI: ${npi}` : ''}
                    <i
                      className='ml-2 font-12 fa fa-clipboard'
                      style={{ color: blue[400] }}
                    />
                    {showNpiCopyMessage &&
                      <div
                        style={{
                          position: 'absolute',
                          left: '154px',
                          top: '4px',
                          color: green[500]
                        }}
                        className='font-12'
                      >
                        NPI copied to clipboard!
                      </div>
                    }
                  </div>
                </div>
              }
              {/* User card */}
              <div className="fx mt-4 ml-2">
                {/* Avatar */}
                <div className={css(styles.drawerUserCardAvatar)}>
                  <img
                    src={avatarImageUrl}
                    ref={drawerAvatarRef}
                    alt="avatar"
                    className={css(styles.drawerUserCardAvatarImg)}
                    onClick={() => toggleAvatarCropperDrawer(true)}
                  />
                  <div
                    onClick={() => { toggleAvatarCropperDrawer(true); }}
                    className={css(styles.drawerAvatarCamera)}
                  >
                    <i className="fa fa-camera" />
                  </div>
                </div>
                {/* User card displayName & userName */}
                <div className={`ml-2 ${css(styles.drawerUserCardText)}`}>
                  {/* Display Name */}
                  <div style={{ marginTop: '-5px' }}>
                    <TextInputWithLabel
                      value={displayName}
                      titleText="Display Name (2 to 40 characters)*"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={248}
                      onChange={handleOnEditDisplayName}
                    />
                    {/* Error message */}
                    {!isDisplayNameValid && !isDisplayNamePristine
                      ? <div className={css(styles.errMsg)}>
                        Invalid Display Name
                      </div>
                      : null
                    }
                  </div>
                  {/* UserName */}
                  <div
                    style={{
                      marginTop: (!isDisplayNameValid && !isDisplayNamePristine) ? '0px' : '4px'
                    }}
                  >
                    <TextInputWithLabel
                      value={userName}
                      titleText="Username (2 to 20 characters, no spaces)*"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor={blue[500]}
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={248}
                      onChange={handleOnEditUserName}
                    />
                    {/* Error message */}
                    {!isUserNameValid && !isUserNamePristine
                      ? <div className={css(styles.errMsg)}>
                        Invalid User Name
                      </div>
                      : null
                    }
                    {isUserNameValid && !isUserNamePristine && isUserNameDuplicate
                      ? <div className={css(styles.errMsg)}>
                        User Name Taken
                      </div>
                      : null
                    }
                  </div>
                </div>
              </div>
              {/* Primary Address */}
              <div className="mt-4 ml-2">
                {/* Heading */}
                <div className="fx fx__v-center">
                  <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                  <div className="ml-1">
                    Primary Address
                  </div>
                </div>
                {/* Form */}
                <div>
                  {/* Address */}
                  <div className="fx">
                    <div>
                      <TextInputWithLabel
                        value={primaryStreetAddress}
                        titleText="Street"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={284}
                        onChange={handleOnEditPrimaryStreetAddress}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={primarySuite}
                        titleText="Suite"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditPrimarySuite}
                      />
                    </div>
                  </div>
                  {/* City, State and Zip */}
                  <div className="fx mt-2">
                    <div>
                      <TextInputWithLabel
                        value={primaryCity}
                        titleText="City"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={230}
                        onChange={handleOnEditPrimaryCity}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={primaryState}
                        titleText="State"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditPrimaryState}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={primaryzip}
                        titleText="Zip"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditPrimaryZip}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Mailing Address */}
              <div className="mt-4 ml-2">
                {/* Heading */}
                <div className="fx fx__v-center">
                  <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                  <div className="ml-1">
                    Mailing Address
                  </div>
                </div>
                {/* Form */}
                <div>
                  {/* Address */}
                  <div className="fx">
                    <div>
                      <TextInputWithLabel
                        value={mailingStreetAddress}
                        titleText="Street"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={284}
                        onChange={handleOnEditMailingStreetAddress}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={mailingSuite}
                        titleText="Suite"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditMailingSuite}
                      />
                    </div>
                  </div>
                  {/* City, State and Zip */}
                  <div className="fx mt-2">
                    <div>
                      <TextInputWithLabel
                        value={mailingCity}
                        titleText="City"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={230}
                        onChange={handleOnEditMailingCity}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={mailingState}
                        titleText="State"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditMailingState}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={mailingzip}
                        titleText="Zip"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditMailingZip}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Contact */}
              <div className="mt-4 ml-2">
                {/* Heading */}
                <div className="fx fx__v-center">
                  <i className="fa fa-address-card" style={{ fontSize: '22px' }} />
                  <div className="ml-1">Contact</div>
                </div>
                {/* Email */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={email}
                    titleText="Email*"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-envelope'
                    onChange={handleOnEditEmail}
                  />
                  {!isEmailPristine && !isEmailValid
                    ? <div className={css(styles.errMsg)}>Invalid Email</div>
                    : null
                  }
                  {isEmailValid && !isEmailPristine && isEmailDuplicate
                    ? <div className={css(styles.errMsg)}>Email already in use</div>
                    : null
                  }
                </div>
                {/* Mobile */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={mobile}
                    titleText="Mobile # *"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-mobile'
                    onChange={handleOnEditMobile}
                  />
                  {!isMobilePristine && !isMobileValid
                    ? <div className={css(styles.errMsg)}>Invalid Mobile #</div>
                    : null
                  }
                  {isMobileValid && !isMobilePristine && isMobileDuplicate
                    ? <div className={css(styles.errMsg)}>Mobile # already in use</div>
                    : null
                  }
                </div>
                {/* Phone */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={phone}
                    titleText="Phone #"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-phone'
                    onChange={handleOnEditPhone}
                  />
                  {!isPhonePristine && !isPhoneValid && phone.length > 0
                    ? <div className={css(styles.errMsg)}>Invalid Phone #</div>
                    : null
                  }
                </div>
                {/* Fax */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={fax}
                    titleText="Fax #"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-fax'
                    onChange={handleOnEditFax}
                  />
                </div>
                {/* Website */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={website}
                    titleText="Website"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-globe'
                    onChange={handleOnEditWebsite}
                  />
                </div>
              </div>
              {/* Profile description */}
              <div className="mt-4 ml-2">
                <div>Profile Description</div>
                <div className={css(styles.profileTextAreaContainer)}>
                  <textarea
                    className={css(styles.profileTextArea)}
                    placeholder="Write a description"
                    value={profileDescription}
                    onChange={handleOnChangeProfileDescription}
                  />
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
              {/* Required fields msg */}
              <div className={css(styles.criticalSmallText)} >
                *Required fields
              </div>
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSaveUserProfile}
                isDisabled={!canAddUser}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Create User Profile
              </Button>
            </div>
          </div>
        </Drawer>
      }
      {/* Profile banner image cropper */}
      <Drawer
        placement="right"
        visible={isBannerCropperDrawerOpen}
        toggleDrawer={() => toggleBannerCropperDrawer(false)}
        zIndex="2050"
        width={window.innerWidth <= 700 ? window.innerWidth : 358}
      >
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div>Upload Profile Banner</div>
            <i
              className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
              onClick={() => { toggleBannerCropperDrawer(false); }}
            />
          </div>
          {/* Content Area */}
          <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
            {showBannerCropper
              ? <div>
                <Cropper
                  src={bannerImageUrl}
                  style={{ height: 400, width: '100%' }}
                  checkCrossOrigin
                  checkOrientation={false}
                  aspectRatio={6}
                  background={false}
                  viewMode={1}
                  autoCropArea={1}
                  guides={false}
                  ref={bannerCropperRef}
                />
              </div>
              : null
            }
            {!showBannerCropper
              ? <div>
                <FileUploader
                  parentRef={bannerFileInputRef}
                  onDrop={handleOnBannerFileDrop}
                  onChange={handleOnBannerFileSelected}
                />
              </div>
              : null
            }
            {isBannerUploading
              ? <div className={css(styles.imageUploadLoader)}>
                <Loader iconType="cog" iconSize="5x" text="Uploading Banner Image" />
              </div>
              : null
            }
          </div>
          {/* Drawer footer area */}
          <div
            className={`fx pr-2 ${css(styles.drawerFooter)}`}
            style={{ position: 'absolute', bottom: '0' }}
          >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickBannerCrop}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Crop Banner Image
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Profile avatar image cropper */}
      <Drawer
        placement="right"
        visible={isAvatarCropperDrawerOpen}
        toggleDrawer={() => toggleAvatarCropperDrawer(false)}
        zIndex="2050"
        width={window.innerWidth <= 700 ? window.innerWidth : 358}
      >
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div>Upload Profile Avatar</div>
            <i
              className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
              onClick={() => { toggleAvatarCropperDrawer(false); }}
            />
          </div>
          {/* Content Area */}
          <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
            {showAvatarCropper
              ? <div>
                <Cropper
                  src={avatarImageUrl}
                  style={{ height: 400, width: '100%' }}
                  checkCrossOrigin
                  checkOrientation={false}
                  aspectRatio={1}
                  background={false}
                  viewMode={1}
                  autoCropArea={1}
                  guides={false}
                  ref={avatarCropperRef}
                />
              </div>
              : null
            }
            {!showAvatarCropper
              ? <div>
                <FileUploader
                  parentRef={avatarFileInputRef}
                  onDrop={handleOnAvatarFileDrop}
                  onChange={handleOnAvatarFileSelected}
                />
              </div>
              : null
            }
            {isAvatarUploading
              ? <div className={css(styles.imageUploadLoader)}>
                <Loader iconType="cog" iconSize="5x" text="Uploading Avatar Image" />
              </div>
              : null
            }
          </div>
          {/* Drawer footer area */}
          <div
            className={`fx pr-2 ${css(styles.drawerFooter)}`}
            style={{ position: 'absolute', bottom: '0' }}
          >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickAvatarCrop}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Crop Avatar Image
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Delete record warning dialog */}
      {isDeleteWarningDialogOpen
        ? <DialogBox
          initWidth={DIALOG_WIDTH}
          top={window.innerWidth <= 576 ? 48 : null}
          left={window.innerWidth <= 576 ? 8 : null}
          initHeight={378}
          isOpen={isDeleteWarningDialogOpen}
          onClose={() => { toggleDeleteWarningDialog(false); }}
          maskBackground="#004080"
          maskOpacity={0.5}
          showHeader
          header={{
            text: 'Delete User Record Warning',
            backColor: salmon[500],
            foreColor: salmon[500],
            overrideTheme: true,
            closeColor: '#FF6060',
            fontStyle: 'bold',
            fontSize: '20px',
            icon: 'fa fa-warning'
          }}
          closeOnMaskClick={false}
        >
          <div className='p-2'>
            {/* Warning message */}
            <div className='pt-2 pb-2'>
              Are you sure about deleting user record?
            </div>
            <div
              className='fx mt-4 pt-2'
              style={{
                justifyContent: 'flex-end',
                borderTop: `1px solid ${gray[500]}`
              }}
            >
              <Button
                btnType='primary'
                isOutlineBtn
                className='mr-2'
                onClick={() => toggleDeleteWarningDialog(false)}
              >
                Cancel
              </Button>
              <Button
                btnType='warning'
                onClick={handleOnDeleteUser}
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogBox>
        : null
      }

      {/* Grid toolbar and users data grid */}
      <div className={css(styles.gridContainer)}>
        {/* Grid toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Grid toolbar - left aligned search */}
          <div className='fx' style={{ alignItems: 'center' }}>
            <TextInputWithLabel
              placeholder='Search Users'
              placeholderColor={gray[200]}
              className='mb-3'
              titleText=''
              value={userSearchTerm}
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={359}
              onChange={handleOnEditUserSearchTerm}
              showClearFieldIcon
              background='#194877'
            />
            <div
              className='ml-2 fx fx__hv-center'
              style={{
                width: '32px',
                height: '32px',
                background: blue[400]
                // marginTop: '17px'
              }}
              onClick={handleOnClickUserSearch}
            >
              <i
                style={{ color: 'white' }} className='fa fa-search'
                onClick={handleOnClickUserSearch}
              />
            </div>
          </div>
          {/* Grid toolbar - right aligned buttons */}
          <div className='fx'>
            {/* Cards grid */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickToggleGrid}
            >
              <i className={`fa fa-lg ${showCardsGrid ? 'fa-bars' : 'fa-th-large'}`} />
            </div>
            {/* Add user button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickToggleUserDrawer}
            >
              <i className="fa fa-lg fa-plus-circle" />
            </div>
            {/* Download */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickDownloadData}
            >
              <i className="fa fa-lg fa-cloud-download" />
            </div>
            {/* Trash bin */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-trash" />
            </div>
            {/* Checkbox */}
            <div className={css(styles.gridToolbarIcon)}>
              {/* <i className="fa fa-lg fa-check-square" /> */}
              <Checkbox
                text=''
                isChecked={selectAllRecords}
                onChange={() => { setSelectAllRecords(!selectAllRecords); }}
              />
            </div>
            {/* Reset button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickResetFilters}
            >
              <i className="fa fa-lg fa-refresh" />
            </div>
            {/* Vertical ellipsis */}
            <div
              className={css(styles.gridToolbarIcon)}
              style={{ fontSize: '16px' }}
            >
              <span className='fa-stack'>
                <i className='fa fa-circle fa-stack-2x' />
                <i
                  className='fa fa-ellipsis-v fa-stack-1x'
                  style={{ color: gray[700] }}
                />
                {/* <i class="fa fa-flag fa-stack-1x fa-inverse"></i> */}
              </span>
            </div>
          </div>
        </div>
        {/* Grid ellipsis menu */}
        <div
          ref={gridEllipsisMenu}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenu}
            onOutsideClick={() => { toggleGridEllipsisMenu(false); }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Show details menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickShowDetailsMenuItem}
              >
                Show Details
              </div>
              {/* Delete record menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickDeleteMenuItem}
              >
                Delete Record
              </div>
              {/* Send Message */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickSendMessage}
              >
                Send Message
              </div>
            </div>
          </PopupMenu>
        </div>
        {/* Users' Data Grid */}
        {!showCardsGrid
          ? <DataGrid
            loader={showLoader}
            icons={icons}
            headerDefs={gridHeaderDefs}
            rowData={gridData}
            filters={filters}
            gridWidth={gridWidth} // was 550
            gridHeight={gridHeight}
            gridHeaderHeight={48}
            gridFooterHeight={0}
            gridContainerHeight='730px'
            rowHeight={48}
            rowsPerPage={DOCS_PER_PAGE}
            defaultCellStyle={defaultCellStyle}
            showFooter={false}
            showPager
            showEllipsisCol
            enableHover
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            isMobileLayout={isMobileLayout} // window.innerWidth <= 700
            canSelectRows
            onClickCell={handleOnClickCell}
            onPopupMenu={handleOnPopupMenu}
            onFilterTypeUpdate={handleFilterTypeUpdate}
            onApplyFilter={handleOnApplyFilter}
            onColFilterToggle={handleOnFilterToggle}
            onColPinChange={handleOnPinChange}
            onLoadPage={handleOnLoadPage}
            onSort={handleOnSortOrderChanged}
            onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
            onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
            onResize={() => { console.warn('Handler not set for onResize prop.'); }}
            onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
            onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
            onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
            onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
          />
          : <CardsGrid
            rowData={gridData}
            gridWidth={gridWidth}
            gridHeight={gridHeight}
            loader={showLoader}
            style={{ height: 'calc(100% - 73px)', overflowY: 'auto' }}
            cardHeight='75px'
            cardWidth='320px'
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            onLoadPage={handleOnLoadPage}
            cardRenderer={(data) => {
              const tmpName = data.name.split('|');
              const avatarImageUrl = tmpName[tmpName.length - 1];
              const nameArr = tmpName[0].split('/');
              const name = nameArr[0];
              return (
                <div
                  style={{
                    background: gray[600],
                    margin: '4px 0',
                    height: '75px',
                    display: 'flex',
                    padding: '4px'
                  }}
                >
                  <div
                    style={{ width: '50px', display: 'flex', alignItems: 'center' }}
                  >
                    <img
                      src={avatarImageUrl}
                      style={{
                        width: '50px',
                        height: 'auto',
                        maxWidth: '50px'
                      }}
                    />
                  </div>
                  <div className='ml-2'>
                    <div style={{ fontWeight: 'bold' }}>
                      {`${name} ${data.credentials !== 'N/A' ? data.credentials : ''}`}
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      {data.mailingAddress}
                    </div>
                  </div>
                </div>
              );
            }}
          />
        }
      </div>
    </div>
  );
});

function mapStateToProps({ user }) {
  return { ...user };
}

export default connect(mapStateToProps, { addAppAlert, toggleDmDrawer, selectDmUser })(UsersDataGrid);
